import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Download, X } from 'lucide-react';
import api from "../../utils/api";

const EmployeeFilePreviewModal = ({ file, onClose }) => {
    const [signedUrl, setSignedUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSignedUrl();
    }, [file]);

    const fetchSignedUrl = async () => {
        try {
            setLoading(true);
            // Use employee endpoint instead of company endpoint
            const response = await api.get(`/files/employee/${file.id}/signed-url`);

            if (response.data.signedUrl) {
                setSignedUrl(response.data.signedUrl);
            }
        } catch (error) {
            console.error('Error fetching signed URL:', error);
            toast.error('Failed to load file preview');
        } finally {
            setLoading(false);
        }
    };

    const canPreview = () => {
        const previewableTypes = [
            'application/pdf',
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
            'image/webp',
            'text/plain',
        ];
        return previewableTypes.includes(file.file_type);
    };

    const renderPreview = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            );
        }

        if (!signedUrl) {
            return (
                <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                    <div className="text-6xl mb-4">❌</div>
                    <p>Failed to load file preview</p>
                </div>
            );
        }

        if (!canPreview()) {
            return (
                <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                    <div className="text-6xl mb-4">📄</div>
                    <p className="text-lg font-medium mb-2">Preview not available</p>
                    <p className="text-sm mb-4">This file type cannot be previewed in the browser</p>
                    <a
                        href={signedUrl}
                        download={file.file_name}
                        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
                    >
                        Download File
                    </a>
                </div>
            );
        }

        // PDF Preview
        if (file.file_type === 'application/pdf') {
            return (
                <iframe
                    src={signedUrl}
                    className="w-full h-[70vh] border-0"
                    title={file.file_name}
                />
            );
        }

        // Image Preview
        if (file.file_type.startsWith('image/')) {
            return (
                <div className="flex items-center justify-center p-4 bg-gray-50">
                    <img
                        src={signedUrl}
                        alt={file.file_name}
                        className="max-w-full max-h-[70vh] object-contain"
                    />
                </div>
            );
        }

        // Text Preview
        if (file.file_type === 'text/plain') {
            return (
                <iframe
                    src={signedUrl}
                    className="w-full h-[70vh] border-0 bg-white p-4"
                    title={file.file_name}
                />
            );
        }

        return (
            <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                <div className="text-6xl mb-4">📄</div>
                <p>Preview not available for this file type</p>
            </div>
        );
    };

    const handleDownload = () => {
        if (signedUrl) {
            const link = document.createElement('a');
            link.href = signedUrl;
            link.download = file.file_name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Download started');
        }
    };

    const getCategoryLabel = (category) => {
        const labels = {
            'company-wide': 'Company-Wide',
            'role-specific': 'Role-Specific',
            'employee-specific': 'For You',
        };
        return labels[category] || category;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                        <h2 className="text-xl font-bold text-gray-900 truncate" title={file.file_name}>
                            {file.file_name}
                        </h2>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                            <span>
                                {file.file_size
                                    ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB`
                                    : 'Unknown size'}
                            </span>
                            <span>•</span>
                            <span>Uploaded: {new Date(file.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownload}
                            disabled={!signedUrl}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <Download size={20} />
                            Download
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Preview Content */}
                <div className="flex-1 overflow-auto">
                    {renderPreview()}
                </div>

                {/* Footer with file info */}
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                            <span className="text-gray-600">
                                <span className="font-medium">Category:</span>{' '}
                                <span className="capitalize">{getCategoryLabel(file.file_category)}</span>
                            </span>
                            {file.role_specific && (
                                <span className="text-gray-600">
                                    <span className="font-medium">Role:</span> {file.role_specific}
                                </span>
                            )}
                        </div>
                        <span className="text-gray-500">
                            Phase: <span className="capitalize font-medium">{file.phase}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeFilePreviewModal;
